import React, { useState } from 'react'
import { Card, Button, Input, Select, Table, Td, StatusBadge, Badge, Notification } from './ui'
import { AGENTS, AM_LIST, CALL_OUTCOMES, LEAD_STATUSES, CALL_PURPOSES } from '../lib/constants'
import { exportCallLogs } from '../lib/export'
import { Trash2, Eye, Pencil, Zap } from 'lucide-react'
import LogCall from './LogCall'
import { planCallSave } from '../lib/workflow'

const today = new Date().toISOString().slice(0, 10)

const empty = {
  call_date: today, call_time: '09:00', agent: 'Halimah',
  prospect_name: '', title: '', company: '', industry: '',
  outcome: CALL_OUTCOMES[0], bant_score: 0, lead_status: 'Contacted',
  meeting_set: 'No', passed_to_am: '', notes: '', follow_up_date: '', next_action: '',
  call_purpose: '', event_name: '',
}

export default function CallLog({ callLogs, addCallLog, updateCallLog, deleteCallLog, canEdit,
  addActivity, addTaskIdempotent, tasks = [], cancelTask }) {
  const [form, setForm] = useState(empty)
  const [saving, setSaving] = useState(false)
  const [notif, setNotif] = useState('')
  const [filterAgent, setFilterAgent] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [viewCall, setViewCall] = useState(null)
  const [showQuickLog, setShowQuickLog] = useState(false)

  // Outcome-driven save: writes call log + activity + idempotent follow-up task,
  // and cancels incompatible outreach tasks. See lib/workflow.js.
  const handleQuickSave = async ({ outcomeId, form: f, lead }) => {
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' })
    const nowTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' })
    const plan = planCallSave({ outcomeId, form: f, lead, todayStr })

    // 1) persist the call activity (existing call_logs shape; no follow_up_date => no legacy reminder)
    const callRow = await addCallLog({
      call_date: todayStr, call_time: nowTime,
      agent: lead.agent, prospect_name: lead.prospect_name, company: lead.company,
      title: '', industry: '',
      outcome: plan.callLogPatch.outcome,
      bant_score: plan.callLogPatch.bant_score ?? 0,
      lead_status: plan.callLogPatch.lead_status || 'Contacted',
      meeting_set: plan.callLogPatch.meeting_set || 'No',
      passed_to_am: '',
      notes: f.notes || f.reason || f.need || '',
      follow_up_date: '', next_action: plan.callLogPatch.next_action || f.next_action || '',
      // tags: why the call + where the lead came from (empty event_name = organic)
      call_purpose: lead.call_purpose || null,
      event_name: lead.lead_origin === 'Event' ? (lead.event_name || null) : null,
    })
    // 2) append-only history
    if (addActivity) await addActivity({ ...plan.activity, call_log_id: callRow?.id || null })
    // 3) idempotent follow-up task
    if (plan.task && addTaskIdempotent) await addTaskIdempotent({ ...plan.task, call_log_id: callRow?.id || null })
    // 4) cancel incompatible outreach tasks (Invalid / Do Not Contact)
    if (plan.cancelOutreach && cancelTask) {
      const norm = s => (s || '').trim().toLowerCase()
      const toCancel = tasks.filter(t => t.status === 'open'
        && plan.outreachTaskTypes.includes(t.task_type)
        && norm(t.company_name) === norm(lead.company))
      for (const t of toCancel) await cancelTask(t.id, plan.suppress ? 'Do Not Contact' : 'Invalid contact')
    }
    setNotif('Call tersimpan — follow-up & history otomatis diproses.')
  }
  const [editCall, setEditCall] = useState(null)   // record yang sedang diedit
  const [savingEdit, setSavingEdit] = useState(false)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const setEdit = (k, v) => setEditCall(c => ({ ...c, [k]: v }))

  const handleUpdate = async () => {
    if (!editCall.prospect_name || !editCall.company) {
      setNotif('Prospect name and company are required.')
      return
    }
    setSavingEdit(true)
    try {
      // Kirim hanya kolom data (buang id & created_at biar aman).
      const { id, created_at, ...updates } = editCall
      await updateCallLog(id, { ...updates, bant_score: parseInt(updates.bant_score) || 0 })
      setNotif('Call log updated!')
      setEditCall(null)
    } catch (e) {
      setNotif('Error: ' + e.message)
    } finally {
      setSavingEdit(false)
    }
  }

  const handleSubmit = async () => {
    if (!form.prospect_name || !form.company) {
      setNotif('Prospect name and company are required.')
      return
    }
    setSaving(true)
    try {
      await addCallLog({ ...form, bant_score: parseInt(form.bant_score) })
      setNotif(form.follow_up_date ? 'Call log saved! Follow-up reminder created.' : 'Call log saved!')
      setForm(empty)
    } catch (e) {
      setNotif('Error: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this call log?')) return
    await deleteCallLog(id)
    setNotif('Call log deleted.')
  }

  const filtered = callLogs.filter(r =>
    (!filterAgent || r.agent === filterAgent) &&
    (!filterStatus || r.lead_status === filterStatus)
  )

  return (
    <div>
      <Notification message={notif} onClose={() => setNotif('')} />

      {showQuickLog && (
        <LogCall onClose={() => setShowQuickLog(false)} onSave={handleQuickSave} />
      )}

      {/* Prominent outcome-based quick logger */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button variant="primary" onClick={() => setShowQuickLog(true)}>
          <Zap size={14} /> Quick Log Call
        </Button>
      </div>

      {viewCall && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Call Details</div>
              <button onClick={() => setViewCall(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            {[
              { label: 'Date', value: viewCall.call_date },
              { label: 'Time', value: viewCall.call_time },
              { label: 'Agent', value: viewCall.agent },
              { label: 'Prospect', value: viewCall.prospect_name },
              { label: 'Title / Role', value: viewCall.title },
              { label: 'Company', value: viewCall.company },
              { label: 'Industry', value: viewCall.industry },
              { label: 'Outcome', value: viewCall.outcome },
              { label: 'BANT Score', value: viewCall.bant_score != null ? `${viewCall.bant_score}/4` : '' },
              { label: 'Lead Status', value: viewCall.lead_status },
              { label: 'Meeting Set', value: viewCall.meeting_set },
              { label: 'Passed to AM', value: viewCall.passed_to_am },
              { label: 'Follow-up Date', value: viewCall.follow_up_date },
              { label: 'Next Action', value: viewCall.next_action },
            ].filter(f => f.value).map(f => (
              <div key={f.label} style={{ display: 'flex', padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 13 }}>
                <span style={{ minWidth: 110, color: '#888', fontWeight: 500 }}>{f.label}</span>
                <span style={{ color: '#222' }}>{f.value}</span>
              </div>
            ))}
            {viewCall.notes && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>Notes / Remarks</div>
                <div style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#333', lineHeight: 1.5 }}>
                  {viewCall.notes}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <Button onClick={() => setViewCall(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal — semua field bisa diubah, tersimpan ke Supabase & reflect ke semua halaman */}
      {editCall && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Edit Call Log</div>
              <button onClick={() => setEditCall(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Select label="Agent" value={editCall.agent} onChange={e => setEdit('agent', e.target.value)}>
                {AGENTS.map(a => <option key={a}>{a}</option>)}
              </Select>
              <Input label="Date" type="date" value={editCall.call_date || ''} onChange={e => setEdit('call_date', e.target.value)} />
              <Input label="Time" type="time" value={editCall.call_time || ''} onChange={e => setEdit('call_time', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Input label="Prospect Name" value={editCall.prospect_name || ''} onChange={e => setEdit('prospect_name', e.target.value)} />
              <Input label="Title / Role" value={editCall.title || ''} onChange={e => setEdit('title', e.target.value)} />
              <Input label="Company" value={editCall.company || ''} onChange={e => setEdit('company', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Input label="Industry" value={editCall.industry || ''} onChange={e => setEdit('industry', e.target.value)} />
              <Select label="Outcome" value={editCall.outcome || CALL_OUTCOMES[0]} onChange={e => setEdit('outcome', e.target.value)}>
                {CALL_OUTCOMES.map(o => <option key={o}>{o}</option>)}
              </Select>
              <Select label="BANT Score (0–4)" value={editCall.bant_score ?? 0} onChange={e => setEdit('bant_score', e.target.value)}>
                {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}{n === 4 ? ' — Fully Qualified' : ''}</option>)}
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Select label="Lead Status" value={editCall.lead_status || LEAD_STATUSES[0]} onChange={e => setEdit('lead_status', e.target.value)}>
                {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
              </Select>
              <Select label="Meeting Set?" value={editCall.meeting_set || 'No'} onChange={e => setEdit('meeting_set', e.target.value)}>
                <option>No</option><option>Yes</option>
              </Select>
              <Select label="Passed to AM" value={editCall.passed_to_am || ''} onChange={e => setEdit('passed_to_am', e.target.value)}>
                <option value="">— None —</option>
                {AM_LIST.map(a => <option key={a}>{a}</option>)}
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Input label="Notes" value={editCall.notes || ''} onChange={e => setEdit('notes', e.target.value)} style={{ flex: 3 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <Input label="Follow-up Date (optional)" type="date" value={editCall.follow_up_date || ''} onChange={e => setEdit('follow_up_date', e.target.value)} />
              <Input label="Next Action" value={editCall.next_action || ''} onChange={e => setEdit('next_action', e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Button onClick={() => setEditCall(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleUpdate} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.85rem' }}>Log New Call</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Select label="Agent" value={form.agent} onChange={e => set('agent', e.target.value)}>
            {AGENTS.map(a => <option key={a}>{a}</option>)}
          </Select>
          <Input label="Date" type="date" value={form.call_date} onChange={e => set('call_date', e.target.value)} />
          <Input label="Time" type="time" value={form.call_time} onChange={e => set('call_time', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Prospect Name" value={form.prospect_name} placeholder="Contact name" onChange={e => set('prospect_name', e.target.value)} />
          <Input label="Title / Role" value={form.title} placeholder="IT Manager" onChange={e => set('title', e.target.value)} />
          <Input label="Company" value={form.company} placeholder="PT ..." onChange={e => set('company', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Industry" value={form.industry} placeholder="Manufacturing, Fintech..." onChange={e => set('industry', e.target.value)} />
          <Select label="Outcome" value={form.outcome} onChange={e => set('outcome', e.target.value)}>
            {CALL_OUTCOMES.map(o => <option key={o}>{o}</option>)}
          </Select>
          <Select label="BANT Score (0–4)" value={form.bant_score} onChange={e => set('bant_score', e.target.value)}>
            {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}{n === 4 ? ' — Fully Qualified' : ''}</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Select label="Lead Status" value={form.lead_status} onChange={e => set('lead_status', e.target.value)}>
            {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Select label="Meeting Set?" value={form.meeting_set} onChange={e => set('meeting_set', e.target.value)}>
            <option>No</option><option>Yes</option>
          </Select>
          <Select label="Passed to AM" value={form.passed_to_am} onChange={e => set('passed_to_am', e.target.value)}>
            <option value="">— None —</option>
            {AM_LIST.map(a => <option key={a}>{a}</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Select label="Tujuan call" value={form.call_purpose} onChange={e => set('call_purpose', e.target.value)}>
            <option value="">— pilih —</option>
            {CALL_PURPOSES.map(p => <option key={p}>{p}</option>)}
          </Select>
          <Input label="Nama event (jika dari event)" value={form.event_name} placeholder="Kosongkan jika organic" onChange={e => set('event_name', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Notes" value={form.notes} placeholder="Call summary..." onChange={e => set('notes', e.target.value)} style={{ flex: 3 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Follow-up Date (optional)" type="date" value={form.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} />
          <Input label="Next Action" value={form.next_action} placeholder="Send email, set meeting..." onChange={e => set('next_action', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={() => setForm(empty)}>Reset</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : '+ Save Call'}
          </Button>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Call Log ({filtered.length})
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All agents</option>
              {AGENTS.map(a => <option key={a}>{a}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All statuses</option>
              {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <Button size="sm" variant="success" onClick={() => exportCallLogs(callLogs)}>Export</Button>
          </div>
        </div>
        <Table headers={['Date', 'Agent', 'Prospect', 'Company', 'Outcome', 'BANT', 'Status', 'Meeting', 'AM', 'Follow-up', 'Actions']}>
          {filtered.length === 0 ? (
            <tr><td colSpan={11} style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>No call logs yet. Add your first call above!</td></tr>
          ) : filtered.map(r => (
            <tr key={r.id}>
              <Td style={{ whiteSpace: 'nowrap' }}>{r.call_date}</Td>
              <Td>{r.agent}</Td>
              <Td>
                <div style={{ fontWeight: 600 }}>{r.prospect_name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{r.title}</div>
              </Td>
              <Td>{r.company}</Td>
              <Td style={{ fontSize: 11 }}>{r.outcome}</Td>
              <Td style={{ textAlign: 'center', fontWeight: 600 }}>{r.bant_score}/4</Td>
              <Td><StatusBadge status={r.lead_status} /></Td>
              <Td style={{ textAlign: 'center' }}>
                {r.meeting_set === 'Yes' ? <Badge variant="success">Yes</Badge> : '–'}
              </Td>
              <Td style={{ fontSize: 11 }}>{r.passed_to_am || '–'}</Td>
              <Td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.follow_up_date || '–'}</Td>
              <Td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setViewCall(r)}
                    title="View details"
                    style={{ background: '#F0FAF5', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#1D9E75', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                    <Eye size={11} /> View
                  </button>
                  {canEdit && (
                    <button onClick={() => setEditCall(r)}
                      title="Edit"
                      style={{ background: '#EAF2FB', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#185FA5', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => handleDelete(r.id)}
                      title="Delete"
                      style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', color: '#999' }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  )
}
